/**
 * Single source of truth for status category → display (color, CSS class, chart code).
 * Categories come from the API assessment.status.category field:
 *   EU: StatusCalculator::BAND_LABELS (benchmark quintiles)
 *   UN: distance-to-target labels (Close to target, Good progress, …)
 */

/** Map status category to chart band code (0 = no data, 1 = worst … 5 = best). */
export function statusCategoryToCode(category: string | null | undefined): number {
  if (!category) return 0;
  const cat = String(category).toLowerCase();

  if (
    cat.includes('insufficient') ||
    cat.includes('not applicable') ||
    cat.includes('unavailable')
  ) {
    return 0;
  }

  // EU benchmark quintiles (StatusCalculator::BAND_LABELS) — specific before generic
  if (cat.includes('highest')) return 5;
  if (cat.includes('upper-middle')) return 4;
  if (cat.includes('lower-middle')) return 2;
  if (cat.includes('middle 20') || (cat.includes('middle') && cat.includes('20%'))) return 3;

  // UN distance-to-target
  if (cat.includes('close') || cat.includes('achieved')) return 5;
  if (cat.includes('good')) return 4;
  if (cat.includes('moderate')) return 3;
  if (cat.includes('very far')) return 1;
  if (cat.includes('far')) return 2;

  // EU / legacy aliases
  if (cat.includes('lowest')) return 1;
  if (cat.includes('fourth')) return 4;
  if (cat.includes('second')) return 2;
  if (cat.includes('top')) return 5;

  return 0;
}

/** @deprecated Use statusCategoryToCode */
export const euStatusCategoryToCode = statusCategoryToCode;

/** Canonical EU band labels (StatusCalculator::BAND_LABELS). */
export const EU_STATUS_LABELS: Record<number, string> = {
  0: 'No status / insufficient data',
  1: 'Lowest 20%',
  2: 'Lower-middle 20%',
  3: 'Middle 20%',
  4: 'Upper-middle 20%',
  5: 'Highest 20%',
};

const STATUS_COLORS: Record<number, string> = {
  0: '#eef2f7',
  1: '#ef4444',
  2: '#f97316',
  3: '#f59e0b',
  4: '#84cc16',
  5: '#16a34a',
};

/** @deprecated Use getStatusColor */
export const EU_STATUS_COLORS: Record<number, string> = STATUS_COLORS;

const STATUS_CIRCLE_CLASSES: Record<number, string> = {
  0: 'status-circle-not-applicable',
  1: 'status-circle-very-far',
  2: 'status-circle-far',
  3: 'status-circle-moderate',
  4: 'status-circle-good',
  5: 'status-circle-close',
};

/** Hex fill color for status circles and bars. */
export function getStatusColor(category: string | null | undefined): string {
  return STATUS_COLORS[statusCategoryToCode(category)] ?? STATUS_COLORS[0];
}

/** CSS class for `.status-circle` elements (sharedStyles.css). */
export function getStatusCircleClass(category: string | null | undefined): string {
  return STATUS_CIRCLE_CLASSES[statusCategoryToCode(category)] ?? STATUS_CIRCLE_CLASSES[0];
}

export function isEuStatusUnavailable(category: string | null | undefined): boolean {
  return statusCategoryToCode(category) === 0;
}
